import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import DiamondMark from "@/components/DiamondMark";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/ventures", label: "Ventures" },
  { to: "/services", label: "Services" },
  { to: "/partnerships", label: "Partnerships" },
  { to: "/acquisitions", label: "Acquisitions" },
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="public-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-bd-black/90 backdrop-blur-xl border-b border-white/10" : "bg-gradient-to-b from-bd-black/70 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <DiamondMark size={30} />
            <span className="text-bd-ivory leading-none">
              <span className="block font-serif text-lg tracking-wide">Black Diamond</span>
              <span className="block overline text-bd-gold text-[0.6rem]">Ventures Group</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide link-underline transition-colors duration-300 ${
                    isActive ? "text-bd-gold" : "text-bd-ivory/80 hover:text-bd-ivory"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="border border-bd-gold text-bd-gold hover:bg-bd-gold hover:text-bd-black transition-colors duration-300 px-5 py-2 text-sm tracking-wide"
            >
              Enquire
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-bd-ivory p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="lg:hidden bg-bd-black border-t border-white/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3 border-b border-white/5 text-base tracking-wide ${
                      isActive ? "text-bd-gold" : "text-bd-ivory/80"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
